/**
 * Strings of scheduled events (2026-10-29): the header's "N events", the
 * list and an event's page, "Interested", the create and edit dialog, the
 * "happening now" strip on a channel, the start notice, the role editor's
 * two permissions and the audit log's lines.
 */
import type { Dict } from './core';

export const eventsEn: Dict = {
  // the server header and the sidebar
  'ev.title': 'Events',
  'ev.count': { one: '{n} event', other: '{n} events' },
  'ev.none': 'Events',
  'ev.open': 'Open the events list',
  'ev.live': 'Happening now',
  'ev.liveIn': 'Happening now in {where}',
  'ev.liveTip': 'Happening now: {name}',
  // the list
  'ev.create': 'Create event',
  'ev.empty.title': 'No upcoming events',
  'ev.empty.body': 'Schedule an event in a voice channel, a stage, or somewhere else.',
  'ev.empty.member': 'When someone schedules an event, it shows up here.',
  'ev.back': 'Back to events',
  // times (the viewer's own time)
  'ev.when.today': 'Today at {time}',
  'ev.when.tomorrow': 'Tomorrow at {time}',
  'ev.when.other': '{date} at {time}',
  'ev.when.range': '{start} – {end}',
  'ev.when.started': 'Started {ago}',
  'ev.when.late': 'Was due {ago}',
  'ev.tz': 'Times are in your time zone ({tz}).',
  // where
  'ev.where.voice': 'Voice channel',
  'ev.where.stage': 'Stage channel',
  'ev.where.external': 'Somewhere else',
  'ev.where.gone': 'A channel you can’t see',
  // interested
  'ev.interested': 'Interested',
  'ev.interestedOn': 'Interested',
  'ev.interestedA11yOn': 'Interested in {name}: on',
  'ev.interestedA11yOff': 'Mark yourself interested in {name}',
  'ev.interestedCount': { one: '{n} interested', other: '{n} interested' },
  'ev.people': 'Interested people',
  'ev.people.none': 'Nobody yet. Be the first.',
  'ev.people.more': 'Show more',
  'ev.by': 'Created by {name}',
  // running it
  'ev.start': 'Start event',
  /** The card's own button (short: it shares a phone's row with Interested). */
  'ev.startShort': 'Start',
  'ev.end': 'End event',
  'ev.join': 'Join',
  'ev.edit': 'Edit event',
  'ev.cancel': 'Cancel event',
  'ev.more': 'More options for {name}',
  'ev.cancel.title': 'Cancel “{name}”?',
  'ev.cancel.body': 'The event is removed for everyone, and the people interested in it lose it.',
  'ev.cancel.go': 'Cancel event',
  'ev.cancel.keep': 'Keep it',
  'ev.end.title': 'End “{name}”?',
  'ev.end.body': 'The event ends for everyone. People can stay in the channel.',
  'ev.end.go': 'End event',
  'ev.started': 'The event has started.',
  'ev.ended': 'The event has ended.',
  'ev.canceled': 'The event was cancelled.',
  'ev.saved': 'Event saved.',
  'ev.created': 'Event created.',
  // the start notice
  'ev.notice': '“{name}” is starting now',
  'ev.notice.os': 'An event you’re interested in is starting',
  'ev.notice.view': 'View',
  // the editor
  'ev.editor.new': 'Create an event',
  'ev.editor.edit': 'Edit event',
  'ev.editor.where': 'Where is your event?',
  'ev.editor.voiceSub': 'Hang out with voice, video and screen share.',
  'ev.editor.stageSub': 'Talk to an audience.',
  'ev.editor.externalSub': 'A place, a link, anywhere.',
  'ev.editor.noRoom': 'You can’t make events there.',
  'ev.editor.channel': 'Channel',
  'ev.editor.location': 'Location',
  'ev.editor.locationPh': 'Add a place or a link',
  'ev.editor.name': 'Event topic',
  'ev.editor.namePh': 'What’s your event?',
  'ev.editor.startDate': 'Start date',
  'ev.editor.startTime': 'Start time',
  'ev.editor.endDate': 'End date',
  'ev.editor.endTime': 'End time',
  'ev.editor.endOptional': 'An end time is optional for a channel event.',
  'ev.editor.addEnd': 'Add an end time',
  'ev.editor.removeEnd': 'No end time',
  'ev.editor.description': 'Description',
  'ev.editor.descriptionPh': 'Tell people a little more about your event.',
  'ev.editor.left': { one: '{n} character left', other: '{n} characters left' },
  'ev.editor.cover': 'Cover image',
  'ev.editor.coverAdd': 'Add a cover image',
  'ev.editor.coverChange': 'Change',
  'ev.editor.coverRemove': 'Remove',
  'ev.editor.coverHint': 'Wide images work best (5:2).',
  'ev.editor.create': 'Create event',
  'ev.editor.save': 'Save',
  'ev.editor.err.name': 'Give your event a topic.',
  'ev.editor.err.when': 'Pick a start date and time.',
  'ev.editor.err.past': 'The start time has to be in the future.',
  'ev.editor.err.end': 'The end has to be after the start.',
  'ev.editor.err.needEnd': 'An event somewhere else needs an end time.',
  'ev.editor.err.location': 'Say where it happens.',
  'ev.editor.err.channel': 'Pick a channel.',
  'ev.editor.err.field': '{message}',
  // errors the server may answer
  'ns.err.10070': 'That event no longer exists.',
  'ns.err.30038': 'This server has reached its limit of events. End or cancel one first.',
  'ns.err.180000': 'This event has already finished.',
  'cfg.off.scheduled_events': 'Events are turned off by the admins right now.',
  // the role and channel editors
  'ns.perm.CREATE_EVENTS': 'Create Events',
  'ns.permd.CREATE_EVENTS':
    'Lets members schedule events, and edit, start, end or cancel the ones they made.',
  'ns.perm.MANAGE_EVENTS': 'Manage Events',
  'ns.permd.MANAGE_EVENTS': 'Lets members edit, start, end or cancel anyone’s events.',
  // the audit log
  'ev.audit.create': '{actor} created the event {name}',
  'ev.audit.update': '{actor} updated the event {name}',
  'ev.audit.start': '{actor} started the event {name}',
  'ev.audit.end': '{actor} ended the event {name}',
  'ev.audit.cancel': '{actor} cancelled the event {name}',
  'ev.audit.delete': '{actor} deleted the event {name}',
  'ev.audit.f.create': 'New events',
  'ev.audit.f.update': 'Event changes',
  'ev.audit.f.delete': 'Deleted events',
};
